import * as React from "react";

import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Spinner } from "./spinner";

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  heightClasses?: string;
  disabled?: boolean;
  isLoading?: boolean;
  hasData?: boolean;
  noDataMessage?: string;
}

interface TableProps extends React.HTMLAttributes<HTMLTableElement> {
  heightClasses?: string;
  disabled?: boolean;
  isLoading?: boolean;
  isLoadingMore?: boolean; // <-- nuevo
  loadMoreRef?: React.Ref<HTMLDivElement>; // <-- nuevo
  hasData?: boolean;
  noDataMessage?: string;
  colSpan?: number; // <-- nuevo, para el colspan del spinner
  overlay?: React.ReactNode;
}

const Table = React.forwardRef<HTMLTableElement, TableProps>(
  (
    {
      className,
      disabled,
      isLoading,
      isLoadingMore,
      loadMoreRef,
      hasData,
      noDataMessage,
      overlay,
      ...props
    },
    ref,
  ) => (
    <div className="relative flex flex-1 flex-col min-h-0 overflow-y-auto">
      <div>
        <table
          ref={ref}
          className={cn(
            "text-sm md:text-base",
            className,
            disabled && "pointer-events-none opacity-50",
          )}
          {...props}
        />
      </div>

      {overlay}

      {isLoading ? (
        // Spinner inicial - ocupa todo el espacio disponible
        <div className="flex flex-1 justify-center items-center">
          <Spinner />
        </div>
      ) : (
        <>
          {!hasData && (
            <div className="flex flex-1 justify-center items-center text-base text-gr-1">
              {noDataMessage}
            </div>
          )}
          {/* Sentinel + spinner para load more */}
          <div
            ref={loadMoreRef}
            className="flex justify-center items-center py-2 min-h-[40px]"
          >
            {isLoadingMore && <Spinner />}
          </div>
        </>
      )}
    </div>
  ),
);
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={cn(
      "sticky top-0 z-40 bg-slate-100 shadow-bottom-custom-dark",
      className,
    )}
    {...props}
  />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0 bg-white", className)}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "sticky bottom-0 z-50 rounded-md border-t border-bg+2 dark:border-bg+1 bg-slate-100 font-medium [&>tr]:last:border-b-0",
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "dark:border-bg+1 transition-colors data-[state=selected]:bg-ac-4 cursor-default",
      // "hover:bg-gray-100",
      // "border-b border-gray-200",
      className,
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    // border
    // border-gr-4
    className={cn(
      `
      border-b 
      border-b-gray-200"
      whitespace-nowrap
      p-1 
      lg:p-2 
      text-left 
      text-sm    
      align-middle
      font-medium 
      text-muted-foreground 
      [&:has([role=checkbox])]:pr-0 
      [&>[role=checkbox]]:translate-y-[2px]

      `,
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement> & {
    tooltip?: React.ReactNode;
    tooltipProps?: React.ComponentPropsWithoutRef<typeof TooltipContent>;
  }
>(
  ({ className, tooltip, tooltipProps, ...props }, ref) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <td
          ref={ref}
          className={cn(
            "text-base lg:text-sm p-1 align-middle [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
            className,
          )}
          {...props}
        />
      </TooltipTrigger>
      {tooltip && <TooltipContent {...tooltipProps}>{tooltip}</TooltipContent>}
    </Tooltip>
  ),
);
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-muted-foreground", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
